import type { ComponentProps } from './componentProps'

export type VisualizerVariant = ComponentProps<'audio-visualizer'>['variant']

/**
 * The numbers behind each visualizer look (#546). A look is what a creator
 * picks; these are not editable and not stored, so retuning one here moves
 * every project that uses it. The bar looks are the v1 layouts' styles,
 * merged where they differed by a pixel or two; the ring scales with its box
 * at the Avatar layout's proportions (bars from 29 % out, 2.5 % thick).
 */
export type VisualizerGeometry = {
  gapSize: number
  lineThickness: number
  roundness: number
  placement: 'over' | 'under' | 'middle'
  mirror: boolean
  innerRadius: number
}

type BarLook = Omit<VisualizerGeometry, 'innerRadius'>

const BAR_LOOKS: Partial<Record<VisualizerVariant, BarLook>> = {
  /** What every visualizer drew before the looks existed. */
  bars: {
    gapSize: 6,
    lineThickness: 8,
    roundness: 4,
    placement: 'middle',
    mirror: false,
  },
  /** BigTitle, ScriptSmall. */
  bold: {
    gapSize: 10,
    lineThickness: 12,
    roundness: 6,
    placement: 'middle',
    mirror: false,
  },
  /** Edgy. */
  thin: {
    gapSize: 10,
    lineThickness: 5,
    roundness: 2,
    placement: 'middle',
    mirror: false,
  },
  /** Block, Chatbox, SelfPortrait: a spectrum standing on the floor. */
  equalizer: {
    gapSize: 4,
    lineThickness: 5,
    roundness: 1,
    placement: 'under',
    mirror: false,
  },
  /** ScriptLarge. */
  blocks: {
    gapSize: 0,
    lineThickness: 20,
    roundness: 0,
    placement: 'under',
    mirror: false,
  },
  /** Framed, Postcard: the spectrum and its reflection. */
  mirrored: {
    gapSize: 12,
    lineThickness: 7,
    roundness: 3,
    placement: 'middle',
    mirror: true,
  },
}

export function visualizerGeometry(
  variant: VisualizerVariant,
  width: number,
  height: number,
): VisualizerGeometry {
  const side = Math.min(width, height)
  const innerRadius = Math.round(side * 0.29)
  if (variant === 'radial') {
    return {
      gapSize: 7,
      lineThickness: Math.max(3, Math.round(side * 0.025)),
      roundness: 7,
      placement: 'middle',
      mirror: false,
      innerRadius,
    }
  }
  return { ...(BAR_LOOKS[variant] ?? BAR_LOOKS.bars!), innerRadius }
}
